"use client";

import React from "react";
import { ClipboardList, Bot, ShieldCheck, Send, DollarSign, Activity } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  id: "submit-info",
  title: "Submit Information",
  description: "Provide your basic health details and symptoms through our secure, HIPAA-compliant intake portal to begin your journey.",
  icon: ClipboardList,
  metrics: "2 Minute Form"
}, {
  id: "clinical-review",
  title: "Clinical Screening",
  description: "Our automated screening tools analyze your specific risk factors to prioritize your care pathway and treatment options.",
  icon: Bot,
  metrics: "Instant Analysis"
}, {
  id: "benefits-verification",
  title: "Benefits Verification",
  description: "Our dedicated team works directly with your insurance provider to confirm coverage for sleep apnea oral appliances.",
  icon: ShieldCheck,
  metrics: "Major Plans Accepted"
}, {
  id: "specialist-match",
  title: "Specialist Matching",
  description: "We connect you with a board-certified sleep specialist in your local area for a comprehensive, medical-grade consultation.",
  icon: Send,
  metrics: "Top 5% Providers"
}, {
  id: "care-coordination",
  title: "Care Coordination",
  description: "Finalize your consultation details and prepare for a custom-fitted solution designed for maximum comfort and rest.",
  icon: DollarSign,
  metrics: "Seamless Process"
}];
export default function TheRevenueLifecycle() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="177425" id="verification-steps" className="bg-background text-foreground relative py-24 overflow-hidden" ref={ref}>
            {}
            <div className="absolute inset-0 z-0 pointer-events-none opacity-5 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612128/img/medical_facility_blueprint_bg.png)] bg-cover bg-center mix-blend-screen" />

            {}
            <div className="absolute inset-0 z-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--color-primary)_0%,_transparent_70%)] opacity-5" />

            <div className={`container mx-auto px-4 relative z-10 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                {}
                <div className="text-center mb-20 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
                        <Activity className="w-4 h-4" />
                        <span className="text-sm font-medium tracking-wide uppercase">Simple 5-Step Process</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-sans font-bold mb-6 tracking-tight">
                        Your Path to <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-2">Restorative Sleep</span>
                    </h2>
                    <p className="text-lg text-muted-foreground">
                        Getting started is simple. We handle the complexities of insurance and specialist matching so you can focus on breathing easier and sleeping better.
                    </p>
                </div>

                {}
                <div className="relative max-w-6xl mx-auto">
                    <ScrollArea className="w-full whitespace-nowrap rounded-2xl border border-border/50 bg-card/30 backdrop-blur-xl shadow-2xl pb-12 pt-20">
                        <div className="flex w-max min-w-full items-center justify-between px-16 md:px-32 h-[240px] relative">

                            {}
                            <div className="absolute top-1/2 left-16 right-16 h-1 -translate-y-1/2 bg-muted rounded-full overflow-hidden">
                                <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-primary/20 via-primary to-chart-2/20 animate-pulse shadow-[0_0_15px_var(--color-primary)]" />
                            </div>

                            {}
                            {stages.map((stage, i) => <div key={stage.id} data-index={i} className="relative z-10 flex flex-col items-center justify-center group mx-10 md:mx-16">
                                    <HoverCard openDelay={100} closeDelay={100}>
                                        <HoverCardTrigger asChild>
                                            <Button variant="outline" size="icon" className="relative flex h-20 w-20 rounded-full border-2 border-primary bg-card shadow-lg transition-all duration-500 hover:scale-110 hover:border-chart-2 hover:bg-card focus-visible:ring-chart-2 group-hover:shadow-[0_0_30px_var(--color-chart-2)]">
                                                <stage.icon className="h-8 w-8 text-primary transition-colors duration-300 group-hover:text-chart-2" />
                                                
                                                {}
                                                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping -z-10" style={{
                      animationDuration: '3s',
                      animationDelay: `${i * 0.4}s`
                    }} />
                                            </Button>
                                        </HoverCardTrigger>
                                        <HoverCardContent side="top" align="center" sideOffset={32} className="w-80 bg-popover/95 backdrop-blur-xl border-border/50 shadow-2xl shadow-primary/10 p-5">
                                            <div className="space-y-4">
                                                <div className="flex items-start gap-4 border-b border-border/50 pb-4">
                                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                                        <stage.icon className="h-6 w-6" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <h4 className="text-base font-semibold text-foreground leading-none">{stage.title}</h4>
                                                        <span className="inline-block text-xs font-medium text-chart-2 bg-chart-2/10 px-2 py-0.5 rounded-full">
                                                            {stage.metrics}
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-muted-foreground leading-relaxed whitespace-normal">
                                                    {stage.description}
                                                </p>
                                            </div>
                                        </HoverCardContent>
                                    </HoverCard>

                                    {}
                                    <div className="absolute top-full mt-8 text-center w-40 -ml-10 left-1/2">
                                        <span className="text-sm font-semibold tracking-wide text-muted-foreground transition-colors duration-300 group-hover:text-primary">
                                            {stage.title}
                                        </span>
                                    </div>
                                </div>)}
                        </div>
                        <ScrollBar orientation="horizontal" className="bg-muted/50 h-2.5" />
                    </ScrollArea>
                </div>
            </div>
        </section>;
}